import {
    toTemporalZonedDateTime,
    startOf,
    endOf,
    toDate as temporalToDate,
} from "../../../shared/lib/temporal";
import { Direction } from "../../../types/Direction";
import { Mode } from "../../../types/Mode";

export function compareDateHour(x, y) {
    if (x.date.value < y.date.value) {
        return -1;
    } else if (x.date.value > y.date.value) {
        return 1;
    } else {
        if (x.hour.value < y.hour.value) {
            return -1;
        } else if (x.hour.value > y.hour.value) {
            return 1;
        }
    }

    return 0;
}

export function arrayBufferToBase64(buffer) {
    let binary = "";
    let bytes = new Uint8Array(buffer);
    let len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

export function normalizeDeviceCounts(deviceCount, selectedDate, timezone) {
    return deviceCount.counts.filter((count) => {
        const start = temporalToDate(
            startOf(toTemporalZonedDateTime(selectedDate, timezone), "day"),
        );
        const end = temporalToDate(
            endOf(toTemporalZonedDateTime(selectedDate, timezone), "day"),
        );
        const ts = temporalToDate(
            toTemporalZonedDateTime(new Date(count.timestamp), timezone),
        ).getTime();
        return start.getTime() <= ts && end.getTime() >= ts;
    });
}

export function getSummaryCount(deviceCounts) {
    const isUnidirectional =
        deviceCounts.device.configs?.[0]?.directionality === "UNIDIRECTIONAL";
    const direction =
        deviceCounts.device.configs?.[0]?.directions?.dir1 === "UPSTREAM"
            ? Direction.Upstream
            : Direction.Downstream;
    const mode = deviceCounts.device.configs?.[0]?.mode;

    const reducedCount = deviceCounts.counts
        .filter((item) => {
            if (item.isPlaceholder) return false;
            if (isUnidirectional) {
                return (
                    item.cumulative?.[direction] !== undefined &&
                    item.cumulative?.[direction] !== null
                );
            }
            return (
                item.cumulative?.Net?.Predicted !== undefined &&
                item.cumulative?.Net?.Predicted !== null
            );
        })
        .at(-1);

    let displayCount = null;

    if (isUnidirectional) {
        if (reducedCount?.cumulative) {
            displayCount = {
                [direction]: {
                    Predicted: Math.floor(
                        reducedCount.cumulative[direction].Predicted ?? 0,
                    ),
                    LowerBound: Math.floor(
                        reducedCount.cumulative[direction].LowerBound ?? 0,
                    ),
                    Adjusted: Math.floor(
                        reducedCount.cumulative[direction].Adjusted ?? 0,
                    ),
                    UpperBound: Math.floor(
                        reducedCount.cumulative[direction].UpperBound ?? 0,
                    ),
                },
            };
        } else {
            displayCount = null;
        }
    } else if (mode === "VIDEO_ONLY") {
        displayCount = {
            [Mode.VideoOnly]: {
                Predicted: "-",
                LowerBound: "-",
                Adjusted: "-",
                UpperBound: "-",
            },
        };
    } else if (reducedCount?.cumulative) {
        displayCount = {
            Net: {
                Predicted: Math.floor(
                    reducedCount.cumulative.Net.Predicted ?? 0,
                ),
                LowerBound: Math.floor(
                    reducedCount.cumulative.Net.LowerBound ?? 0,
                ),
                Adjusted: Math.floor(reducedCount.cumulative.Net.Adjusted ?? 0),
                UpperBound: Math.floor(
                    reducedCount.cumulative.Net.UpperBound ?? 0,
                ),
            },
            Upstream: {
                Predicted: Math.floor(
                    reducedCount.cumulative.Upstream.Predicted ?? 0,
                ),
                LowerBound: Math.floor(
                    reducedCount.cumulative.Upstream.LowerBound ?? 0,
                ),
                Adjusted: Math.floor(
                    reducedCount.cumulative.Upstream.Adjusted ?? 0,
                ),
                UpperBound: Math.floor(
                    reducedCount.cumulative.Upstream.UpperBound ?? 0,
                ),
            },
            Downstream: {
                Predicted: Math.floor(
                    reducedCount.cumulative.Downstream.Predicted ?? 0,
                ),
                LowerBound: Math.floor(
                    reducedCount.cumulative.Downstream.LowerBound ?? 0,
                ),
                Adjusted: Math.floor(
                    reducedCount.cumulative.Downstream.Adjusted ?? 0,
                ),
                UpperBound: Math.floor(
                    reducedCount.cumulative.Downstream.UpperBound ?? 0,
                ),
            },
        };
    } else {
        displayCount = null;
    }

    return {
        device: deviceCounts.device,
        count: displayCount,
        timestamp: reducedCount?.timestamp,
    };
}
